import { useState, useEffect, useCallback, useMemo } from 'react';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

const RATIOS = ['1:1', '2:3', '16:9'] as const;
const COUNTS = [2, 4] as const;

type Product = 'books' | 'dialogs' | 'drills' | 'stories' | 'dramas';

interface ImageStudioProps {
  product: Product;
  productKey: string;
  defaultPrompt?: string;
  onSelect: (url: string, slot: string) => void;
  slots?: Array<{ value: string; label: string }>;
  languageCode?: string;
}

interface PromptFields {
  nationality: string;
  gender: string;
  ethnicity: string;
  hair: string;
  nations: string;
  clothing: string;
  age: string;
  pose: string;
  location: string;
  city: string;
  season: string;
  timeOfDay: string;
  time: string;
  blur: string;
  grading: string;
  emotion: string;
}

interface LangDefaults {
  nationality: string;
  ethnicity: string;
  nations: string;
  clothing: string;
  location: string;
  city: string;
}

const LANG_DEFAULTS: Record<string, LangDefaults> = {
  RUS: { nationality: 'Russian', ethnicity: 'Russian', nations: 'Russian', clothing: 'national costume', location: "Basil's Cathedral", city: 'Moscow' },
  UKR: { nationality: 'Ukrainian', ethnicity: 'Ukrainian', nations: 'Ukrainian', clothing: 'vyshyvanka embroidered blouse', location: 'Saint Sophia Cathedral', city: 'Kyiv' },
  BEL: { nationality: 'Belarusian', ethnicity: 'Belarusian', nations: 'Belarusian', clothing: 'traditional linen outfit', location: 'National Library', city: 'Minsk' },
  POL: { nationality: 'Polish', ethnicity: 'Polish', nations: 'Polish', clothing: 'traditional folk dress', location: 'Old Town Square', city: 'Warsaw' },
  CES: { nationality: 'Czech', ethnicity: 'Czech', nations: 'Czech', clothing: 'traditional kroj costume', location: 'Charles Bridge', city: 'Prague' },
  SLK: { nationality: 'Slovak', ethnicity: 'Slovak', nations: 'Slovak', clothing: 'traditional kroj costume', location: 'Bratislava Castle', city: 'Bratislava' },
  SRP: { nationality: 'Serbian', ethnicity: 'Serbian', nations: 'Serbian', clothing: 'traditional folk dress', location: 'Kalemegdan Fortress', city: 'Belgrade' },
  HRV: { nationality: 'Croatian', ethnicity: 'Croatian', nations: 'Croatian', clothing: 'traditional folk costume', location: 'Dubrovnik Old Town walls', city: 'Dubrovnik' },
  SLV: { nationality: 'Slovenian', ethnicity: 'Slovenian', nations: 'Slovenian', clothing: 'traditional folk dress', location: 'Lake Bled', city: 'Ljubljana' },
  BUL: { nationality: 'Bulgarian', ethnicity: 'Bulgarian', nations: 'Bulgarian', clothing: 'traditional folk costume', location: 'Alexander Nevsky Cathedral', city: 'Sofia' },
  MKD: { nationality: 'Macedonian', ethnicity: 'Macedonian', nations: 'Macedonian', clothing: 'traditional folk dress', location: 'Old Bazaar', city: 'Skopje' },
  RON: { nationality: 'Romanian', ethnicity: 'Romanian', nations: 'Romanian', clothing: 'traditional ie blouse', location: 'Palace of the Parliament', city: 'Bucharest' },
  HUN: { nationality: 'Hungarian', ethnicity: 'Hungarian', nations: 'Hungarian', clothing: 'traditional folk dress', location: 'Fisherman\'s Bastion', city: 'Budapest' },
  DEU: { nationality: 'German', ethnicity: 'German', nations: 'German', clothing: 'smart casual outfit', location: 'Brandenburg Gate', city: 'Berlin' },
  FRA: { nationality: 'French', ethnicity: 'French', nations: 'French', clothing: 'chic Parisian outfit', location: 'Eiffel Tower', city: 'Paris' },
  ITA: { nationality: 'Italian', ethnicity: 'Italian', nations: 'Italian', clothing: 'elegant Italian outfit', location: 'the Colosseum', city: 'Rome' },
  SPA_ES: { nationality: 'Spanish', ethnicity: 'Spanish', nations: 'Spanish', clothing: 'traditional flamenco dress', location: 'Plaza de España', city: 'Seville' },
  SPA_MX: { nationality: 'Mexican', ethnicity: 'Mexican', nations: 'Mexican', clothing: 'traditional embroidered dress', location: 'Palacio de Bellas Artes', city: 'Mexico City' },
  SPA_AR: { nationality: 'Argentine', ethnicity: 'Argentine', nations: 'Argentine', clothing: 'smart casual outfit', location: 'La Boca neighborhood', city: 'Buenos Aires' },
  POR_BR: { nationality: 'Brazilian', ethnicity: 'Brazilian', nations: 'Brazilian', clothing: 'casual tropical outfit', location: 'Christ the Redeemer', city: 'Rio de Janeiro' },
  POR_PT: { nationality: 'Portuguese', ethnicity: 'Portuguese', nations: 'Portuguese', clothing: 'smart casual outfit', location: 'Belém Tower', city: 'Lisbon' },
  NLD: { nationality: 'Dutch', ethnicity: 'Dutch', nations: 'Dutch', clothing: 'smart casual outfit', location: 'a canal bridge', city: 'Amsterdam' },
  SWE: { nationality: 'Swedish', ethnicity: 'Swedish', nations: 'Swedish', clothing: 'Scandinavian-style outfit', location: 'Gamla Stan', city: 'Stockholm' },
  NOR: { nationality: 'Norwegian', ethnicity: 'Norwegian', nations: 'Norwegian', clothing: 'bunad national costume', location: 'Bryggen waterfront', city: 'Bergen' },
  DAN: { nationality: 'Danish', ethnicity: 'Danish', nations: 'Danish', clothing: 'Scandinavian-style outfit', location: 'Nyhavn waterfront', city: 'Copenhagen' },
  FIN: { nationality: 'Finnish', ethnicity: 'Finnish', nations: 'Finnish', clothing: 'Nordic casual outfit', location: 'Helsinki Cathedral', city: 'Helsinki' },
  ISL: { nationality: 'Icelandic', ethnicity: 'Icelandic', nations: 'Icelandic', clothing: 'Icelandic wool sweater', location: 'Hallgrímskirkja', city: 'Reykjavik' },
  ELL: { nationality: 'Greek', ethnicity: 'Greek', nations: 'Greek', clothing: 'Mediterranean casual outfit', location: 'the Parthenon', city: 'Athens' },
  TUR: { nationality: 'Turkish', ethnicity: 'Turkish', nations: 'Turkish', clothing: 'modern casual outfit', location: 'Hagia Sophia', city: 'Istanbul' },
  ENG_US: { nationality: 'American', ethnicity: 'American', nations: 'American', clothing: 'casual outfit', location: 'Central Park', city: 'New York' },
  ENG_GB: { nationality: 'British', ethnicity: 'British', nations: 'British', clothing: 'smart casual outfit', location: 'Tower Bridge', city: 'London' },
  ENG_AU: { nationality: 'Australian', ethnicity: 'Australian', nations: 'Australian', clothing: 'casual outdoor outfit', location: 'Sydney Opera House', city: 'Sydney' },
  JPN: { nationality: 'Japanese', ethnicity: 'Japanese', nations: 'Japanese', clothing: 'kimono', location: 'Fushimi Inari shrine', city: 'Kyoto' },
  KOR: { nationality: 'Korean', ethnicity: 'Korean', nations: 'Korean', clothing: 'hanbok', location: 'Gyeongbokgung Palace', city: 'Seoul' },
  CMN_CN: { nationality: 'Chinese', ethnicity: 'Chinese', nations: 'Chinese', clothing: 'qipao', location: 'the Great Wall', city: 'Beijing' },
  CMN_TW: { nationality: 'Taiwanese', ethnicity: 'Taiwanese', nations: 'Taiwanese', clothing: 'modern casual outfit', location: 'Taipei 101', city: 'Taipei' },
  YUE_HK: { nationality: 'Hong Kong', ethnicity: 'Cantonese', nations: 'Hong Kong', clothing: 'modern chic outfit', location: 'Victoria Harbour', city: 'Hong Kong' },
  NAN_TW: { nationality: 'Taiwanese', ethnicity: 'Taiwanese Hokkien', nations: 'Taiwanese', clothing: 'traditional Taiwanese outfit', location: 'Lukang Old Street', city: 'Lukang' },
  HAK_SX: { nationality: 'Hakka', ethnicity: 'Hakka', nations: 'Hakka', clothing: 'traditional Hakka outfit', location: 'a tulou roundhouse', city: 'Meizhou' },
  THA: { nationality: 'Thai', ethnicity: 'Thai', nations: 'Thai', clothing: 'traditional Thai silk outfit', location: 'Wat Arun', city: 'Bangkok' },
  KHM: { nationality: 'Cambodian', ethnicity: 'Khmer', nations: 'Cambodian', clothing: 'traditional sampot', location: 'Angkor Wat', city: 'Siem Reap' },
  VIE: { nationality: 'Vietnamese', ethnicity: 'Vietnamese', nations: 'Vietnamese', clothing: 'áo dài', location: 'Hoàn Kiếm Lake', city: 'Hanoi' },
  MYA: { nationality: 'Burmese', ethnicity: 'Burmese', nations: 'Burmese', clothing: 'traditional longyi and htamein', location: 'Shwedagon Pagoda', city: 'Yangon' },
  IND: { nationality: 'Indonesian', ethnicity: 'Javanese', nations: 'Indonesian', clothing: 'traditional batik outfit', location: 'Borobudur Temple', city: 'Yogyakarta' },
  TGL: { nationality: 'Filipino', ethnicity: 'Filipino', nations: 'Filipino', clothing: 'traditional terno', location: 'Rizal Park', city: 'Manila' },
  HIN: { nationality: 'Indian', ethnicity: 'Indian', nations: 'Indian', clothing: 'traditional sari', location: 'the Taj Mahal', city: 'Agra' },
  BEN: { nationality: 'Bengali', ethnicity: 'Bengali', nations: 'Bengali', clothing: 'traditional sari', location: 'Ahsan Manzil', city: 'Dhaka' },
  TAM: { nationality: 'Tamil', ethnicity: 'Tamil', nations: 'Tamil', clothing: 'traditional silk sari', location: 'Meenakshi Temple', city: 'Madurai' },
  PES: { nationality: 'Persian', ethnicity: 'Persian', nations: 'Persian', clothing: 'traditional Persian outfit', location: 'Nasir al-Mulk Mosque', city: 'Shiraz' },
  ARB: { nationality: 'Arab', ethnicity: 'Arab', nations: 'Arab', clothing: 'traditional thobe and keffiyeh', location: 'Al-Masjid al-Haram', city: 'Mecca' },
  ARZ: { nationality: 'Egyptian', ethnicity: 'Egyptian', nations: 'Egyptian', clothing: 'modern casual outfit', location: 'the Pyramids of Giza', city: 'Cairo' },
  HEB: { nationality: 'Israeli', ethnicity: 'Israeli', nations: 'Israeli', clothing: 'modern casual outfit', location: 'the Western Wall', city: 'Jerusalem' },
  SWH: { nationality: 'Tanzanian', ethnicity: 'East African', nations: 'Tanzanian', clothing: 'traditional kanga wrap', location: 'Stone Town', city: 'Zanzibar' },
  KAT: { nationality: 'Georgian', ethnicity: 'Georgian', nations: 'Georgian', clothing: 'traditional chokha', location: 'Holy Trinity Cathedral', city: 'Tbilisi' },
  HYE: { nationality: 'Armenian', ethnicity: 'Armenian', nations: 'Armenian', clothing: 'traditional taraz', location: 'Republic Square', city: 'Yerevan' },
  KAZ: { nationality: 'Kazakh', ethnicity: 'Kazakh', nations: 'Kazakh', clothing: 'traditional shapan', location: 'Bayterek Tower', city: 'Astana' },
  KHK: { nationality: 'Mongolian', ethnicity: 'Mongolian', nations: 'Mongolian', clothing: 'traditional deel', location: 'Gandan Monastery', city: 'Ulaanbaatar' },
  EKK: { nationality: 'Estonian', ethnicity: 'Estonian', nations: 'Estonian', clothing: 'traditional folk costume', location: 'Tallinn Old Town', city: 'Tallinn' },
  LAV: { nationality: 'Latvian', ethnicity: 'Latvian', nations: 'Latvian', clothing: 'traditional folk costume', location: 'Riga Old Town', city: 'Riga' },
  LIT: { nationality: 'Lithuanian', ethnicity: 'Lithuanian', nations: 'Lithuanian', clothing: 'traditional folk costume', location: 'Gediminas Tower', city: 'Vilnius' },
  CYM: { nationality: 'Welsh', ethnicity: 'Welsh', nations: 'Welsh', clothing: 'traditional Welsh outfit', location: 'Cardiff Castle', city: 'Cardiff' },
  GLA: { nationality: 'Scottish', ethnicity: 'Scottish', nations: 'Scottish', clothing: 'tartan outfit', location: 'Edinburgh Castle', city: 'Edinburgh' },
  GLE: { nationality: 'Irish', ethnicity: 'Irish', nations: 'Irish', clothing: 'Aran sweater outfit', location: 'Temple Bar', city: 'Dublin' },
  EUS: { nationality: 'Basque', ethnicity: 'Basque', nations: 'Basque', clothing: 'traditional Basque outfit', location: 'Guggenheim Museum', city: 'Bilbao' },
  CAT: { nationality: 'Catalan', ethnicity: 'Catalan', nations: 'Catalan', clothing: 'smart Mediterranean outfit', location: 'Sagrada Familia', city: 'Barcelona' },
  AZJ: { nationality: 'Azerbaijani', ethnicity: 'Azerbaijani', nations: 'Azerbaijani', clothing: 'traditional outfit', location: 'Flame Towers', city: 'Baku' },
  UZN: { nationality: 'Uzbek', ethnicity: 'Uzbek', nations: 'Uzbek', clothing: 'traditional ikat chapan', location: 'Registan Square', city: 'Samarkand' },
};

const DEFAULT_FIELDS: PromptFields = {
  nationality: '',
  gender: 'female',
  ethnicity: '',
  hair: 'stylish long hair',
  nations: '',
  clothing: 'national costume',
  age: '23',
  pose: 'standing',
  location: '',
  city: '',
  season: 'summer',
  timeOfDay: 'morning',
  time: '10am',
  blur: 'slightly',
  grading: 'Bright',
  emotion: 'neutral',
};

function buildPrompt(f: PromptFields): string {
  return `An attractive ${f.nationality} ${f.gender} model (${f.ethnicity} ethnicity) with ${f.hair} in ${f.nations} ${f.clothing}, age ${f.age}, ${f.pose} facing the camera, posing in front of ${f.location} in ${f.city} on a ${f.grading.toLowerCase()} ${f.season} ${f.timeOfDay} around ${f.time}.
Cinematic portrait, 85mm lens, shallow depth of field, subject in sharp focus, background ${f.blur} blurred. ${f.grading} color grading.
Camera perspective: looking directly at the camera, making eye contact with the viewer, engaging and direct.
Facial expression: ${f.emotion}`;
}

function fieldsForLang(langCode: string, gender: string): PromptFields {
  const d = LANG_DEFAULTS[langCode];
  return {
    ...DEFAULT_FIELDS,
    gender,
    nationality: d?.nationality ?? '',
    ethnicity: d?.ethnicity ?? '',
    nations: d?.nations ?? '',
    clothing: d?.clothing ?? 'national costume',
    location: d?.location ?? '',
    city: d?.city ?? '',
  };
}

const FIELD_DEFS: Array<{ key: keyof PromptFields; label: string; width: string }> = [
  { key: 'nationality', label: 'Nationality', width: 'sm:w-32' },
  { key: 'gender', label: 'Gender', width: 'sm:w-24' },
  { key: 'ethnicity', label: 'Ethnicity', width: 'sm:w-32' },
  { key: 'hair', label: 'Hair description', width: 'sm:w-40' },
  { key: 'nations', label: "Nation's", width: 'sm:w-28' },
  { key: 'clothing', label: 'Clothing', width: 'sm:w-40' },
  { key: 'age', label: 'Age #', width: 'sm:w-16' },
  { key: 'pose', label: 'Standing/sitting', width: 'sm:w-24' },
  { key: 'location', label: 'At a location', width: 'sm:w-48' },
  { key: 'city', label: 'A city', width: 'sm:w-32' },
  { key: 'season', label: 'Season', width: 'sm:w-24' },
  { key: 'timeOfDay', label: 'Morning/afternoon', width: 'sm:w-28' },
  { key: 'time', label: 'Time', width: 'sm:w-20' },
  { key: 'blur', label: 'Softly/slightly', width: 'sm:w-24' },
  { key: 'grading', label: 'Bright/Warm', width: 'sm:w-24' },
  { key: 'emotion', label: 'Emotion', width: 'sm:w-28' },
];

export default function ImageStudio({ product, productKey, onSelect, slots, languageCode }: ImageStudioProps) {
  const [fields, setFields] = useState<PromptFields>(() => fieldsForLang(languageCode ?? '', 'female'));
  const [promptOverride, setPromptOverride] = useState<string | null>(null);
  const [ratio, setRatio] = useState<string>('2:3');
  const [count, setCount] = useState<number>(2);
  const [selectedSlot, setSelectedSlot] = useState(slots?.[0]?.value || 'cover');
  const [generating, setGenerating] = useState(false);
  const [images, setImages] = useState<Array<{ id: string; url: string }>>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<Array<{ prompt: string; images: Array<{ id: string; url: string }> }>>([]);

  useEffect(() => {
    if (languageCode) {
      setFields(prev => fieldsForLang(languageCode, prev.gender));
      setPromptOverride(null);
    }
  }, [languageCode]);

  const composedPrompt = useMemo(() => buildPrompt(fields), [fields]);
  const prompt = promptOverride ?? composedPrompt;

  const updateField = useCallback((key: keyof PromptFields, value: string) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setPromptOverride(null);
  }, []);

  const handleGenderChange = useCallback((g: string) => {
    setFields(prev => fieldsForLang(languageCode ?? '', g));
    setPromptOverride(null);
  }, [languageCode]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    setImages([]);
    setSelectedImage(null);

    try {
      const res = await fetch(`${BASE_URL}/api/admin/images/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          count,
          product,
          language_code: languageCode,
          gender: fields.gender,
          ethnicity: fields.ethnicity?.toLowerCase().replace(/\s+/g, '-') || 'korean',
          age: fields.age || 'young_adult',
          emotion: fields.emotion || 'neutral',
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${res.status}`);
      }
      const data = await res.json() as { ok: boolean; images: Array<{ id: string; url: string }>; count: number };

      if (data.ok && data.images?.length > 0) {
        setImages(data.images);
        setHistory(prev => [...prev, { prompt, images: data.images }]);
        // Index each generation into the per-language repository so it's browsable later
        // (this shows up under Browse for this book's language only). Fire-and-forget.
        for (const img of data.images) {
          fetch(`${BASE_URL}/api/admin/images/repository`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              language_code: languageCode || 'unknown', image_id: img.id, url: img.url,
              prompt, product, slot: selectedSlot, created_at: new Date().toISOString(),
            }),
          }).catch(() => { /* best-effort index */ });
        }
      } else {
        setError('No images generated');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setGenerating(false);
    }
  };

  const saveSelected = async () => {
    if (!selectedImage) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/images/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: selectedImage,
          product,
          key: productKey,
          slot: `${selectedSlot}_${fields.gender}`,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { url: string };
      onSelect(data.url, `${selectedSlot}_${fields.gender}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-zinc-800 rounded-lg p-4 md:p-6 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="text-zinc-200 font-medium text-sm">Image Studio</h4>
        <div className="text-zinc-500 text-xs">
          {product} / {productKey}
        </div>
      </div>

      {/* Generation controls */}
      <div className="flex flex-wrap gap-3">
        <div className="w-full sm:w-24">
          <label className="text-zinc-500 text-xs block mb-1">Ratio</label>
          <select
            value={ratio}
            onChange={e => setRatio(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
          >
            {RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="w-full sm:w-20">
          <label className="text-zinc-500 text-xs block mb-1">Count</label>
          <select
            value={count}
            onChange={e => setCount(Number(e.target.value))}
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
          >
            {COUNTS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {slots && slots.length > 1 && (
          <div className="w-full sm:w-36">
            <label className="text-zinc-500 text-xs block mb-1">Slot</label>
            <select
              value={selectedSlot}
              onChange={e => setSelectedSlot(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
            >
              {slots.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Prompt field builder */}
      <div>
        <div className="text-zinc-500 text-xs mb-2">Prompt Fields</div>
        <div className="flex flex-wrap gap-2">
          {FIELD_DEFS.map(fd => (
            <div key={fd.key} className={`w-full ${fd.width}`}>
              <label className="text-zinc-600 text-[10px] block mb-0.5">{fd.label}</label>
              {fd.key === 'gender' ? (
                <select
                  value={fields.gender}
                  onChange={e => handleGenderChange(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-200"
                >
                  <option value="female">female</option>
                  <option value="male">male</option>
                </select>
              ) : (
                <input
                  type="text"
                  value={fields[fd.key]}
                  onChange={e => updateField(fd.key, e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-200 focus:border-teal-500 outline-none"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Composed prompt (read-only by default, editable on click) */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-zinc-500 text-xs">Final Prompt</label>
          {promptOverride !== null && (
            <button
              onClick={() => setPromptOverride(null)}
              className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors"
            >
              Reset to fields
            </button>
          )}
        </div>
        <textarea
          value={prompt}
          onChange={e => setPromptOverride(e.target.value)}
          rows={5}
          className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200 font-mono resize-y"
        />
      </div>

      {/* Generate button */}
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={generate}
          disabled={generating || !prompt.trim()}
          className="px-5 py-2.5 rounded bg-violet-700 text-violet-100 text-sm font-medium hover:bg-violet-600 disabled:opacity-50 transition-colors"
        >
          {generating ? 'Generating…' : 'Generate'}
        </button>
        {selectedImage && (
          <button
            onClick={saveSelected}
            disabled={saving}
            className="px-5 py-2.5 rounded bg-emerald-700 text-emerald-100 text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : `Save as ${selectedSlot} (${fields.gender})`}
          </button>
        )}
        {generating && (
          <span className="text-zinc-500 text-xs animate-pulse">
            Generating via IKON (10-30s)…
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded px-3 py-2 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Generated images */}
      {images.length > 0 && (
        <div>
          <div className="text-zinc-500 text-xs mb-2">
            Select an image ({images.length} generated):
          </div>
          <div className={`grid gap-3 ${images.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
            {images.map((img, i) => (
              <button
                key={img.id}
                onClick={() => setSelectedImage(img.url)}
                className={`rounded-lg overflow-hidden border-2 transition-all ${
                  selectedImage === img.url
                    ? 'border-teal-400 ring-2 ring-teal-400/30 scale-[1.02]'
                    : 'border-transparent hover:border-zinc-500'
                }`}
              >
                <img src={img.url} alt={`Generated ${i + 1}`} className="w-full aspect-auto" />
                <div className="bg-zinc-900 px-2 py-1.5 text-xs text-zinc-400 text-center">
                  #{i + 1} {selectedImage === img.url && <span className="text-teal-400">Selected</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 1 && (
        <details className="text-zinc-500 text-xs">
          <summary className="cursor-pointer hover:text-zinc-300 transition-colors">
            Previous generations ({history.length - 1})
          </summary>
          <div className="mt-2 space-y-3">
            {history.slice(0, -1).reverse().map((h, hi) => (
              <div key={hi} className="bg-zinc-900/50 rounded p-3">
                <div className="text-zinc-400 text-xs mb-1 font-mono truncate">{h.prompt.slice(0, 120)}…</div>
                <div className="grid grid-cols-4 gap-2">
                  {h.images.map((img, ii) => (
                    <button
                      key={img.id}
                      onClick={() => { setSelectedImage(img.url); setImages(h.images); }}
                      className={`rounded overflow-hidden border ${
                        selectedImage === img.url ? 'border-teal-400' : 'border-zinc-700 hover:border-zinc-500'
                      }`}
                    >
                      <img src={img.url} alt={`History ${hi}-${ii}`} className="w-full aspect-square object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
